// src/components/layout/Layout.js
import styled from 'styled-components';
import Header from './Header';
import Footer from './Footer';
import MenuBar from './MenuBar';
import AnnouncementBar from './AnnouncementBar';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';

const LayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
  max-width: 100vw;
  min-height: 100vh;
  padding: 0;
  margin: 0;
  overflow-x: hidden;
  overflow-y: auto;
`;

const MainContent = styled.main`
  width: 100%;
  max-width: 100vw;
  padding: 0;
  margin: 0;
  border-bottom: 3px solid ${(props) => props.theme.colors.subBackground};
`;

const BackToTopButton = styled.button`
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 48px;
  height: 48px;
  border: 1px solid #d7dce1;
  border-radius: 50%;
  background: linear-gradient(180deg, #ffffff 0%, #f4f7fa 100%);
  color: #2f3a45;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 10px 24px rgba(16, 24, 40, 0.14),
    0 2px 5px rgba(16, 24, 40, 0.1);
  z-index: 1100;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    opacity 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      0 12px 26px rgba(16, 24, 40, 0.18),
      0 4px 8px rgba(16, 24, 40, 0.14);
  }

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border-top: 2px solid currentColor;
    border-left: 2px solid currentColor;
    transform: rotate(45deg) translate(1px, 2px);
  }

  @media (max-width: 768px) {
    right: 16px;
    bottom: 16px;
    width: 44px;
    height: 44px;
  }
`;

const Layout = ({ children }) => {
  const location = useLocation();
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    // Don't scroll to top if we're returning to home with category scroll flag
    const shouldScrollToCategory = sessionStorage.getItem(
      'scrollToCategoryView',
    );
    if (shouldScrollToCategory !== 'true') {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;

      const scrollableHeight = scrollHeight - clientHeight;

      if (scrollableHeight <= 0) {
        setShowBackToTop(false);
        return;
      }

      const scrollRatio = scrollTop / scrollableHeight;
      const isVisible = scrollRatio >= 0.1;

      setShowBackToTop(isVisible);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <LayoutWrapper>
      <Header />
      <MenuBar />
      <AnnouncementBar />
      <MainContent>{children}</MainContent>
      <Footer />
      {showBackToTop ? (
        <BackToTopButton
          aria-label='To the top'
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      ) : null}
    </LayoutWrapper>
  );
};

export default Layout;
